'use client';

import { memo, type ReactNode, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Auth } from '@air/api';
import { useToasts } from '@air/provider-toast';

import { Button, type ButtonVariants } from '~/components/Button';
import { useSelectedProviderContext } from '~/contexts/SelectedProviderContext';

export type SAMLProvidersFooterProps = {
  /** Content to render below the button (e.g. a back link) */
  footer?: ReactNode;
  /** Callback to navigate to OAuth waiting page with the generated id */
  onNavigate: (id: string) => void;
  /** Button size — defaults to 'large' for backwards compatibility */
  size?: ButtonVariants['size'];
};

export const SAMLProvidersFooter = memo(({ footer, onNavigate, size = 'large' }: SAMLProvidersFooterProps) => {
  const { showToast } = useToasts();
  const { selectedProvider } = useSelectedProviderContext();

  const handleContinue = useCallback(() => {
    if (selectedProvider) {
      const id = uuidv4();

      Auth.ssoSamlSignin(selectedProvider, { id });

      onNavigate(id);
    } else {
      showToast('Please select an SSO provider');
    }
  }, [onNavigate, selectedProvider, showToast]);

  return (
    <div className="flex w-full flex-col items-center gap-3">
      <Button
        appearance="primary"
        className="w-full"
        disabled={!selectedProvider}
        onClick={handleContinue}
        size={size}
        type="button"
      >
        Continue
      </Button>
      {footer}
    </div>
  );
});

SAMLProvidersFooter.displayName = 'SAMLProvidersFooter';
